/**
 * API utility functions for form submissions
 * Handles communication with the backend API endpoints
 */

// Backend API base URL
const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'https://back.recallnepal.com/api';

// Common fetch options for all API requests
const getFetchOptions = (method, data) => ({
  method: method,
  mode: 'cors',
  cache: 'no-cache',
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
  },
  redirect: 'follow',
  referrerPolicy: 'no-referrer',
  body: data ? JSON.stringify(data) : undefined,
});

/**
 * Submit contact form data to the API
 * 
 * @param {Object} formData - Contact form data
 * @param {string} formData.name - Full name
 * @param {string} formData.email - Email address
 * @param {string} formData.phone - Phone number (optional)
 * @param {string} formData.subject - Message subject
 * @param {string} formData.message - Message content
 * @returns {Promise<Object>} Response from the API
 */
export const submitContactForm = async (formData) => {
  try {
    // Save to localStorage immediately as a backup
    try {
      if (typeof localStorage !== 'undefined') {
        const savedForms = JSON.parse(localStorage.getItem('contactForms') || '[]');
        savedForms.push({
          ...formData,
          timestamp: new Date().toISOString(),
          savedAt: 'beforeSubmit'
        });
        localStorage.setItem('contactForms', JSON.stringify(savedForms));
      }
    } catch (storageError) {
      console.warn('Could not save to local storage:', storageError);
    }
    
    // Create the fetch options
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    };
    
    // Array of endpoints to try in order
    const endpoints = [
      'contact_direct.php',  // Try the new ultra-simplified endpoint first
      'contact_new.php',     // Then try the new endpoint 
      'contact_simple.php',  // Then the simplified endpoint
      'cors_test.php'        // Finally the CORS test endpoint
    ];
    
    let lastError = null;
    
    // Try each endpoint in sequence
    for (const endpoint of endpoints) {
      try {
        console.log(`Trying endpoint: ${API_BASE_URL}/${endpoint}`);
        const response = await fetch(`${API_BASE_URL}/${endpoint}`, options);
        
        if (response.ok) {
          console.log(`Endpoint ${endpoint} succeeded`);
          const result = await response.json();
          
          // If using fallback, add to localStorage again
          if (endpoint !== 'contact_new.php') {
            try {
              if (typeof localStorage !== 'undefined') {
                const savedForms = JSON.parse(localStorage.getItem('contactFormsFallback') || '[]');
                savedForms.push({
                  ...formData,
                  timestamp: new Date().toISOString(),
                  endpoint: endpoint
                });
                localStorage.setItem('contactFormsFallback', JSON.stringify(savedForms));
              }
            } catch (e) {
              // Ignore storage errors
            }
          }
          
          return result;
        }
        
        // If we get here, this endpoint failed
        console.warn(`Endpoint ${endpoint} returned status ${response.status}`);
      } catch (error) {
        console.warn(`Error with ${endpoint}:`, error.message);
        lastError = error;
      }
    }
    
    // If we get here, all endpoints failed
    throw lastError || new Error('All contact endpoints failed');
  } catch (error) {
    console.error('Error submitting contact form:', error);
    throw error;
  }
};

/**
 * Submit application form data to the API
 * 
 * @param {Object} formData - Application form data
 * @returns {Promise<Object>} Response from the API
 */
export const submitApplicationForm = async (formData) => {
  try {
    // Log the request - debug
    console.log('Submitting application form to:', `${API_BASE_URL}/apply.php`);
    
    // Create the fetch options
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    };
    
    // Make the fetch request
    const response = await fetch(`${API_BASE_URL}/apply.php`, options);
    
    // Handle the response
    if (!response.ok) {
      console.error('Server returned error status:', response.status);
      const errorText = await response.text();
      console.error('Error response:', errorText);
      
      let errorMessage = `Failed to submit application (Status: ${response.status})`;
      try {
        const errorData = JSON.parse(errorText);
        if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // If parsing fails, use the default error message
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting application form:', error);
    throw error;
  }
};

/**
 * Subscribe to newsletter
 * 
 * @param {string} email - Email address for subscription
 * @returns {Promise<Object>} Response from the API
 */
export const subscribeToNewsletter = async (email) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/newsletter.php`, 
      getFetchOptions('POST', { email })
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to subscribe to newsletter (Status: ${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    throw error;
  }
};

/**
 * Get unsubscribe URL for newsletter
 * 
 * @param {string} email - Email address to unsubscribe
 * @returns {string} Unsubscribe URL
 */
export const getUnsubscribeUrl = (email) => {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  // Simple token generation (this should match the backend logic)
  const token = sha256(`${email}RECALL_SALT_${today}`);
  return `${API_BASE_URL}/newsletter.php?action=unsubscribe&email=${encodeURIComponent(email)}&token=${token}`;
};

/**
 * Simple SHA-256 implementation for browser
 * This is a simplification - in production use a proper crypto library
 * 
 * @param {string} str - String to hash
 * @returns {string} Hashed string
 */
function sha256(str) {
  // This is a placeholder - in a real app, use:
  // import { createHash } from 'crypto' (Node.js)
  // or a browser-compatible library
  
  // For now just return a dummy hash - the actual hash is computed server-side
  return Array.from(str)
    .reduce((hash, char) => (((hash << 5) - hash) + char.charCodeAt(0)) | 0, 0)
    .toString(16);
}

/**
 * Test API connectivity and CORS configuration
 * 
 * @returns {Promise<Object>} Response from the API
 */
export const testConnection = async () => {
  try {
    const timestamp = new Date().getTime(); // Add cache-busting parameter
    const response = await fetch(
      `${API_BASE_URL}/check.php?t=${timestamp}`, 
      {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
      }
    );

    if (!response.ok) {
      throw new Error(`API connection test failed (Status: ${response.status})`);
    }

    return await response.json();
  } catch (error) {
    console.error('API connection test failed:', error);
    throw error;
  }
};

export default {
  submitContactForm,
  submitApplicationForm,
  subscribeToNewsletter,
  getUnsubscribeUrl,
  testConnection
}; 